import React, { useState, useEffect } from 'react';
import { CategoryBrandMapping, UserBrandPreference } from '../types/models';
import { ApiService } from '../services/api';
import { useAppContext } from '../context/AppContext';
import { webSocketService } from '../services/websocket';

interface BrandReselectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: number;
  level3Categories: Array<{ id: number; name: string }>;
  onReselectionComplete: () => void;
}

const BrandReselectionModal: React.FC<BrandReselectionModalProps> = ({
  isOpen,
  onClose,
  applicationId,
  level3Categories,
  onReselectionComplete
}) => {
  const [categoryBrandMappings, setCategoryBrandMappings] = useState<Record<number, string>>({});
  const [availableBrands, setAvailableBrands] = useState<Record<number, string[]>>({});
  const [savedPreferences, setSavedPreferences] = useState<UserBrandPreference[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { userId, setSearchEventHandlers } = useAppContext();

  // Загружаем бренды и предпочтения для каждой категории при открытии модального окна
  useEffect(() => {
    if (isOpen && level3Categories.length > 0) {
      loadBrandsForCategories();
      loadUserPreferences();
      // Сбрасываем состояние обработки при открытии модального окна
      setIsProcessing(false);
    } else if (!isOpen) {
      // Очищаем mappings только при закрытии
      setCategoryBrandMappings({});
    }
  }, [isOpen, level3Categories]);

  const loadUserPreferences = async () => {
    setIsLoadingPreferences(true);
    try {
      const preferences = await ApiService.getUserBrandPreferences();
      setSavedPreferences(preferences);
      console.log('Загружено предпочтений пользователя:', preferences.length);
    } catch (error) {
      console.error('Ошибка при загрузке предпочтений пользователя:', error);
    } finally {
      setIsLoadingPreferences(false);
    }
  };

  const detectPreferenceChanges = (): Array<{ level3_category_id: number; brand_name: string }> => {
    const changes: Array<{ level3_category_id: number; brand_name: string }> = [];
    
    for (const [categoryIdStr, brandName] of Object.entries(categoryBrandMappings)) {
      if (!brandName || brandName.trim() === '') continue;
      
      const categoryId = parseInt(categoryIdStr);
      const existingPref = savedPreferences.find(p => p.level3_category_id === categoryId);
      
      // Если предпочтения нет или оно изменилось
      if (!existingPref || existingPref.brand_name !== brandName) {
        changes.push({ level3_category_id: categoryId, brand_name: brandName });
      }
    }
    
    return changes;
  };

  const loadBrandsForCategories = async () => {
    setIsLoading(true);
    const brandsMap: Record<number, string[]> = {};
    
    try {
      // Получаем бренды для всех категорий через новый API
      for (const category of level3Categories) {
        try {
          const categoryBrands = await ApiService.getBrandsForCategory(category.id);
          brandsMap[category.id] = categoryBrands;
        } catch (error) {
          console.error(`Failed to load brands for category ${category.id}:`, error);
          brandsMap[category.id] = [];
        }
      }
      
      setAvailableBrands(brandsMap);
    } catch (error) {
      console.error('Failed to load brands for categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrandChange = (categoryId: number, brandName: string) => {
    setCategoryBrandMappings(prev => ({
      ...prev,
      [categoryId]: brandName
    }));
  };

  const handleResetBrand = (categoryId: number) => {
    setCategoryBrandMappings(prev => {
      const newMappings = { ...prev };
      delete newMappings[categoryId];
      return newMappings;
    });
  };

  const fillAllPreferences = () => {
    const newMappings: Record<number, string> = { ...categoryBrandMappings };
    let filledCount = 0;
    
    for (const category of level3Categories) {
      const savedPref = savedPreferences.find(p => p.level3_category_id === category.id);
      if (savedPref) {
        // Проверяем, что бренд из предпочтений доступен для этой категории
        const categoryBrands = availableBrands[category.id] || [];
        if (categoryBrands.includes(savedPref.brand_name)) {
          newMappings[category.id] = savedPref.brand_name;
          filledCount++;
        }
      }
    }
    
    setCategoryBrandMappings(newMappings);
    if (filledCount > 0) {
      console.log(`Заполнено ${filledCount} категорий из предпочтений`);
    } else {
      alert('Нет сохранённых предпочтений для этих категорий');
    }
  };

  const fillCategoryPreference = (categoryId: number) => {
    const savedPref = savedPreferences.find(p => p.level3_category_id === categoryId);
    if (savedPref) {
      const categoryBrands = availableBrands[categoryId] || [];
      if (categoryBrands.includes(savedPref.brand_name)) {
        handleBrandChange(categoryId, savedPref.brand_name);
      }
    }
  };

  const hasPreferenceForCategory = (categoryId: number): boolean => {
    const savedPref = savedPreferences.find(p => p.level3_category_id === categoryId);
    if (!savedPref) return false;
    const categoryBrands = availableBrands[categoryId] || [];
    return categoryBrands.includes(savedPref.brand_name);
  };

  const handleClose = () => {
    if (isProcessing) {
      const confirmClose = window.confirm('Переподбор брендов в процессе. Вы уверены, что хотите закрыть окно? Процесс продолжится в фоне.');
      if (!confirmClose) {
        return;
      }
    }
    onClose();
  };

  const handleSavePreferences = async () => {
    const preferencesToSave = Object.entries(categoryBrandMappings)
      .filter(([_, brandName]) => brandName && brandName.trim() !== '')
      .map(([categoryId, brandName]) => ({
        level3_category_id: Number.parseInt(categoryId),
        brand_name: brandName
      }));

    if (preferencesToSave.length === 0) {
      alert('Выберите хотя бы один бренд для сохранения');
      return;
    }

    try {
      await ApiService.saveUserBrandPreferences(preferencesToSave);
      alert('Предпочтения успешно сохранены!');
      // Обновляем сохраненные предпочтения
      await loadUserPreferences();
    } catch (error) {
      console.error('Ошибка при сохранении предпочтений:', error);
      alert('Не удалось сохранить предпочтения. Попробуйте еще раз.');
    }
  };

  const handleStartReselection = async () => {
    const mappings = Object.entries(categoryBrandMappings)
      .filter(([_, brandName]) => brandName && brandName.trim() !== '')
      .map(([categoryId, brandName]) => ({
        category3_id: parseInt(categoryId),
        brand_name: brandName
      }));

    if (mappings.length === 0) {
      alert('Выберите хотя бы один бренд для категории');
      return;
    }

    // Проверяем изменения предпочтений перед запуском
    const changes = detectPreferenceChanges();
    if (changes.length > 0) {
      const shouldSave = window.confirm(
        'Сохранить настройки брендов для будущих подборов?\n' +
        'Эти предпочтения будут автоматически применяться при следующих загрузках спецификаций.'
      );
      
      if (shouldSave) {
        try {
          await ApiService.saveUserBrandPreferences(changes);
          // Обновляем сохраненные предпочтения
          await loadUserPreferences();
        } catch (error) {
          console.error('Ошибка при сохранении предпочтений:', error);
          alert('Ошибка при сохранении предпочтений. Продолжить переподбор?');
        }
      }
    }

    setIsProcessing(true);
    
    // Устанавливаем обработчики WebSocket в AppContext перед запуском
    const handlers = {
      onBrandReselectionComplete: async (appId: number, processed?: number, skipped?: number) => {
        if (appId === applicationId) {
          console.log(`BrandReselectionModal: Получено событие завершения переподбора для заявки ${appId}`);
          
          alert(`Переподбор завершен. Обработано: ${processed || 0}, пропущено: ${skipped || 0}`);
          
          onReselectionComplete();
          // Очищаем обработчики после завершения
          setSearchEventHandlers(null);
        }
      },
      onBrandReselectionError: (appId: number, error?: string) => {
        if (appId === applicationId) {
          console.log(`BrandReselectionModal: Получено событие ошибки переподбора для заявки ${appId}`);
          alert(`Ошибка переподбора: ${error || 'Неизвестная ошибка'}`);
          // Очищаем обработчики после ошибки
          setSearchEventHandlers(null);
        }
      }
    };
    
    setSearchEventHandlers(handlers);
    
    try {
      console.log('BrandReselectionModal: Запуск переподбора брендов...');
      
      // Подключаемся к WebSocket если не подключены
      if (userId) {
        await webSocketService.connect(userId);
      }
      
      await ApiService.startBrandReselection(applicationId, mappings);
      
      // Показываем уведомление пользователю и закрываем модальное окно
      alert('Переподбор брендов запущен. Вы получите уведомление о завершении.');
      onClose();
      
    } catch (error) {
      console.error('Failed to start brand reselection:', error);
      alert('Не удалось запустить переподбор брендов. Попробуйте еще раз.');
      setIsProcessing(false);
      // Очищаем обработчики при ошибке запуска
      setSearchEventHandlers(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-3 sm:p-6">
      <div className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-h-[calc(100dvh-3rem)]">
        <div className="flex flex-shrink-0 items-center justify-between border-b px-4 py-4 sm:px-6">
          <h2 className="text-xl font-bold">Переподбор Бренда</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isProcessing}
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          <div className="mb-4">
            <div className="flex justify-between items-start">
              <p className="text-gray-600 text-sm">
                Выберите бренды для категорий, для которых нужно выполнить переподбор артикулов.
                Будут обработаны только те SPN, у которых текущий выбранный бренд отличается от указанного.
              </p>
              <button
                onClick={fillAllPreferences}
                disabled={isLoading || isLoadingPreferences || savedPreferences.length === 0}
                className="ml-4 px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Заполнить из предпочтений
              </button>
            </div>
          </div>

          {isLoading || isLoadingPreferences ? (
            <div className="flex justify-center py-8">
              <div className="text-gray-500">
                {isLoading && 'Загрузка брендов...'}
                {isLoadingPreferences && 'Загрузка предпочтений...'}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 font-semibold text-sm text-gray-600 border-b pb-2">
                <div>Категория (3 уровня)</div>
                <div>Бренд</div>
              </div>

              {level3Categories.map(category => (
                <div key={category.id} className="grid grid-cols-2 gap-4 items-center py-2 border-b">
                  <div className="text-sm">
                    <span className="font-medium">{category.name}</span>
                    <span className="text-gray-500 text-xs ml-2">(ID: {category.id})</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={categoryBrandMappings[category.id] || ''}
                      onChange={(e) => handleBrandChange(category.id, e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded text-sm"
                      disabled={isProcessing}
                    >
                      <option value="">Выберите бренд...</option>
                      {availableBrands[category.id]?.map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>

                    {hasPreferenceForCategory(category.id) && !categoryBrandMappings[category.id] && (
                      <button
                        onClick={() => fillCategoryPreference(category.id)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 underline whitespace-nowrap"
                        disabled={isProcessing}
                      >
                        из предпочтений
                      </button>
                    )}

                    {categoryBrandMappings[category.id] && (
                      <button
                        onClick={() => handleResetBrand(category.id)}
                        className="px-2 py-1 text-xs text-red-600 hover:text-red-800"
                        disabled={isProcessing}
                      >
                        Сбросить
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 flex-wrap justify-end gap-3 border-t px-4 py-4 sm:px-6">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
            disabled={isProcessing}
          >
            Отмена
          </button>
          
          <button
            onClick={handleSavePreferences}
            disabled={detectPreferenceChanges().length === 0}
            className={`px-4 py-2 text-white rounded ${
              detectPreferenceChanges().length === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            Сохранить предпочтения
          </button>
          
          <button
            onClick={handleStartReselection}
            disabled={isProcessing || Object.keys(categoryBrandMappings).length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Запуск...' : 'Запустить переподбор'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandReselectionModal;
